import { useState, useEffect, useRef } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { useInvoicePayment } from '@/hooks/useInvoicePayment';
import { todayLocalDateString, addLocalDays } from '@/lib/localDate';

interface PaymentModalProps {
  open: boolean;
  invoiceId: string;
  totalAmount: number;
  amountPaid: number;
  // 2026-08-03 reconciliation fix: identity of the invoice being paid.
  // Optional so an older caller still compiles, but BOTH real call sites
  // (InvoiceListPage + InvoiceDetailPage) pass them — see the header comment
  // on the summary block below for why.
  invoiceNumber?: string | null;
  clientName?: string | null;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function PaymentModal({
  open,
  invoiceId,
  totalAmount,
  amountPaid,
  invoiceNumber,
  clientName,
  onOpenChange,
  onSuccess,
}: PaymentModalProps) {
  const [amount, setAmount] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayLocalDateString());
  const [errors, setErrors] = useState<{ amount?: string; payment_date?: string }>({});

  // v0.14 (Dmitri sweep): cap the date input so users can't accidentally
  // record a payment in the year 2099. Backend Pydantic validator also
  // rejects future dates beyond a 1-day grace as a backstop.
  //
  // Polish R2 (Dmitri round 2 — M2): backend allows today+1 grace (UTC/local
  // skew). Frontend max=today created a mismatch at 11:55pm local where the
  // FE blocked something the BE would accept. Loosen FE to today+1 in local
  // time so the bounds match — kinder to a user whose clock is slightly off.
  //
  // 2026-05-30 audit fix: maxDate stays as a label/hint, but we do NOT bind it
  // to the input's HTML `max` attribute anymore. The native rangeOverflow
  // validity check blocked form submission BEFORE React's onSubmit fired, so
  // our user-visible red error never rendered — the modal sat silently and
  // Christa would think the button was broken. Now React does both bounds
  // checks (min + max) in validateForm and surfaces the messages directly.
  const maxDate = addLocalDays(todayLocalDateString(), 1) ?? todayLocalDateString();
  // 2026-05-30 audit fix: minDate guard. Lexicographic "0605-02-26" > "2026-06-01"
  // is FALSE — the previous string compare let through pre-1000 AD years and
  // Chromium's date input accepts year-605 via keystroke sequences. Anchor
  // to 2020 (well before any plausible payment in this system) so a typo
  // can't drop a payment in the Middle Ages. Backend validator now mirrors.
  const minDate = '2020-01-01';

  const { mutate, isPending, error } = useInvoicePayment(invoiceId);

  // 2026-05-30 audit fix: guard against double-submit. button.disabled is set
  // from isPending, but the form's onSubmit can still fire on a rapid second
  // click before React re-renders the button with disabled=true OR via the
  // Enter key which submits regardless of button state. Hold an in-flight
  // flag in a ref so a second handleSubmit while the first is in motion is
  // a no-op even if isPending hasn't propagated yet.
  const submittingRef = useRef(false);

  // SPEC-invoice-merge-history R4 AC5: received money above the total is an overpayment held on the invoice, not a
  // negative balance (a merge can leave cash above the combined total).
  const remainingBalance = Math.max(0, totalAmount - amountPaid);

  // 2026-05-30 audit fix: amount input was a free-text field that called
  // parseFloat directly. parseFloat("1,000") returns 1 silently — Christa
  // pastes from Excel and records a $1000 payment as $1.00. parseFloat
  // "$1,000.00" returns NaN with the misleading error "Amount must be
  // greater than zero" (she typed $1000, why does it say zero?).
  //
  // Strip dollar signs, commas, and whitespace before parsing. After the
  // strip, the remaining string MUST match a strict numeric pattern (digits
  // with at most one decimal point). Anything else returns null so the
  // caller can surface a precise error.
  //
  // Accepts:  "1000" "1,000" "$1,000.00" "1000.50" " 500 " "$  500" "1,00" (→100)
  // Rejects:  "1.234.56" (two decimals)  "1e3" (scientific)
  //           "abc"  "100$"  "-100"  any unicode digits other than 0-9
  // (Note: misplaced commas like "1,00" parse cleanly to 100 — we strip all
  // commas without enforcing thousands-separator placement, which matches
  // intent: a typo shouldn't block the payment, and the user sees the value
  // normalized on blur.)
  const parseAmountStrict = (raw: string): number | null => {
    if (!raw || typeof raw !== 'string') return null;
    const cleaned = raw.replace(/[$,\s]/g, '');
    if (cleaned === '') return null;
    // Strict pattern: optional digits, optional single decimal point, optional digits;
    // at least one digit required overall.
    if (!/^\d+(\.\d+)?$|^\.\d+$/.test(cleaned)) return null;
    const n = Number(cleaned);
    return Number.isFinite(n) ? n : null;
  };

  // Format currency
  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Reset form when modal opens
  useEffect(() => {
    if (open) {
      setAmount('');
      setPaymentDate(todayLocalDateString());
      setErrors({});
    }
  }, [open]);

  const handlePayRemaining = () => {
    setAmount(remainingBalance.toFixed(2));
  };

  const validateForm = () => {
    const newErrors: { amount?: string; payment_date?: string } = {};

    if (!amount || amount.trim() === '') {
      newErrors.amount = 'Amount is required';
    } else {
      const numAmount = parseAmountStrict(amount);
      if (numAmount === null) {
        // 2026-05-30 audit: precise message instead of the old misleading
        // "Amount must be greater than zero" that fired for any non-numeric input
        newErrors.amount = 'Amount must be a valid number (digits with optional decimal — no commas, $, or scientific notation)';
      } else if (numAmount <= 0) {
        newErrors.amount = 'Amount must be greater than zero';
      } else if (remainingBalance > 0 && numAmount - remainingBalance > 0.01) {
        // Theme 5b: BLOCK overpayment. The backend hard-rejects a payment whose
        // total would exceed the invoice (models/invoices.py:931 —
        // `current_amount_paid + payment_amount > total_amount`). This used to be
        // a soft yellow warning the user could submit straight past, eating a
        // 400. Make it a real validation error so the doomed submit never fires.
        // 0.01 tolerance mirrors currency rounding (the backend compares strictly,
        // but a sub-cent over is just float noise and "Pay Remaining" should pass).
        newErrors.amount = `Amount exceeds the remaining balance of ${formatCurrency(remainingBalance)}. Enter ${formatCurrency(remainingBalance)} or less.`;
      }
    }

    if (!paymentDate || paymentDate.trim() === '') {
      newErrors.payment_date = 'Payment date is required';
    } else {
      // 2026-05-30 audit fix: use Date objects, not lexicographic string compare.
      // String compare misses sub-1000 AD years because "0605" < "2026" — a
      // year-605 payment slipped through string compare in staging audit.
      // Validating shape first; ISO-form dates parse safely with new Date(YYYY-MM-DD)
      // (note: that parses as UTC midnight, but we only compare day-resolution
      // values built the same way, so the comparison is consistent).
      const isoShape = /^\d{4}-\d{2}-\d{2}$/.test(paymentDate);
      if (!isoShape) {
        newErrors.payment_date = 'Payment date must be a valid date.';
      } else {
        const entered = new Date(paymentDate);
        const min = new Date(minDate);
        const max = new Date(maxDate);
        if (Number.isNaN(entered.getTime())) {
          newErrors.payment_date = 'Payment date must be a valid date.';
        } else if (entered < min) {
          newErrors.payment_date = `Payment date must be on or after ${minDate}.`;
        } else if (entered > max) {
          // Polish R2 (M2 day-boundary align): match the backend's today+1 grace.
          newErrors.payment_date = 'Payment date cannot be more than 1 day in the future.';
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    // 2026-05-30 audit: re-entry guard. submittingRef beats button.disabled
    // since onSubmit can fire from the Enter key OR a fast second click
    // before React re-renders the button.
    if (submittingRef.current || isPending) return;

    if (!validateForm()) {
      return;
    }

    const numAmount = parseAmountStrict(amount);
    if (numAmount === null) return; // validateForm already set the error

    submittingRef.current = true;

    mutate(
      {
        amount: numAmount,
        payment_date: paymentDate,
      },
      {
        onSuccess: () => {
          submittingRef.current = false;
          onSuccess();
          onOpenChange(false);
        },
        onError: () => {
          submittingRef.current = false;
          // Error is handled by the hook's toast
        },
      }
    );
  };

  const handleAmountChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setAmount(value);

    // Clear amount error when the value parses cleanly to a positive number
    if (value) {
      const numValue = parseAmountStrict(value);
      if (numValue !== null && numValue > 0) {
        setErrors((prev) => ({ ...prev, amount: undefined }));
      }
    }
  };

  const handleAmountBlur = () => {
    if (amount) {
      const numValue = parseAmountStrict(amount);
      if (numValue !== null && numValue > 0) {
        // Normalize display: strip $/commas and pin to 2 decimals.
        // Christa pastes "$1,000.00" → blur normalizes to "1000.00"
        setAmount(numValue.toFixed(2));
      }
    }
  };

  const showOverpaymentWarning = () => {
    if (!amount) return false;
    const numAmount = parseAmountStrict(amount);
    return numAmount !== null && numAmount > remainingBalance;
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {invoiceNumber ? `Record Payment for Invoice #${invoiceNumber}` : 'Record Payment'}
          </DialogTitle>
          <DialogDescription>
            Record a payment for this invoice. Status updates automatically: Partially Paid for partial amounts, Paid when the full balance is received.
          </DialogDescription>
        </DialogHeader>

        {/* 2026-08-03 reconciliation fix: name the invoice being paid. Opened
            from the list's $ action, NOTHING on screen identified the target —
            two live mis-postings resulted (Jul 21: paid 520 instead of 599;
            Aug 3: 656 instead of 657), both times two invoices with the SAME
            amount and similar client names. Same treatment the VOID dialog got
            (InvoiceDetailPage's void-payment-single-summary) so Christa never
            silently pays the wrong row. */}
        {(invoiceNumber || clientName) && (
          <div
            className="rounded-md bg-gray-50 border border-gray-200 px-3 py-2.5 text-sm text-gray-700"
            data-testid="payment-invoice-summary"
          >
            Recording a payment against{' '}
            <span className="font-medium">
              {invoiceNumber ? `Invoice #${invoiceNumber}` : 'this invoice'}
            </span>
            {clientName && (
              <>
                {' '}for <span className="font-medium">{clientName}</span>
              </>
            )}
            {' '}&mdash; total{' '}
            <span className="font-medium">{formatCurrency(totalAmount)}</span>.
          </div>
        )}

        {/* 2026-05-30 audit fix: noValidate so React owns ALL validation
            messaging. Without it, the browser's native rangeOverflow on the
            date input would block submission silently and our React error
            would never render. */}
        <form onSubmit={handleSubmit} noValidate>
          <div className="space-y-4 py-4">
            {/* Amount Input */}
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="amount">Payment Amount</Label>
                <Button
                  type="button"
                  variant="link"
                  size="sm"
                  onClick={handlePayRemaining}
                  className="h-auto p-0 text-xs"
                >
                  Pay Remaining {formatCurrency(remainingBalance)}
                </Button>
              </div>
              <Input
                id="amount"
                type="text"
                inputMode="decimal"
                value={amount}
                onChange={handleAmountChange}
                onBlur={handleAmountBlur}
                placeholder="0.00"
                aria-label="Payment Amount"
              />
              {errors.amount && (
                <p className="text-sm text-red-500">{errors.amount}</p>
              )}
              {showOverpaymentWarning() && !errors.amount && (
                <p className="text-sm text-yellow-600">
                  Warning: Amount exceeds remaining balance ({formatCurrency(remainingBalance)})
                </p>
              )}
              <p className="text-sm text-muted-foreground">
                Remaining balance: {formatCurrency(remainingBalance)}
              </p>
            </div>

            {/* Payment Date */}
            <div className="space-y-2">
              <Label htmlFor="payment_date">Payment Date</Label>
              <Input
                id="payment_date"
                type="date"
                value={paymentDate}
                onChange={(e) => setPaymentDate(e.target.value)}
                // 2026-05-30 audit fix: dropped `max={maxDate}` and `min={minDate}`
                // from the input. Native validity (rangeOverflow/rangeUnderflow)
                // was silently blocking onSubmit before React could render the
                // user-facing error. React now owns the bounds checks; the
                // input is a free-form date.
                aria-label="Payment Date"
              />
              {errors.payment_date && (
                <p className="text-sm text-red-500">{errors.payment_date}</p>
              )}
            </div>

            {/* API Error */}
            {error && (
              <p className="text-sm text-red-500">{error.message || 'Payment failed'}</p>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
              Cancel
            </Button>
            <Button type="submit" disabled={isPending}>
              {isPending ? 'Recording...' : 'Record Payment'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
